import React, { useEffect, useState } from 'react';
import { initGA, setGAUserId } from '../utils/analytics';
import { getEnv } from '../utils/env';
import { useAppSelector } from '../store';

export const CookieConsent: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const gaId = getEnv('VITE_GA_TRACKING_ID');
  const user = useAppSelector((state) => state.auth.user);

  // Do not show the cookie consent dialog when rendering a video
  const isRenderMode = new URLSearchParams(window.location.search).get('render') === 'true';

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (consent === 'accepted') {
      if (gaId) {
        initGA(gaId);
        if (user?.id) {
          setGAUserId(user.id);
        }
      }
    } else if (!consent) {
      // Delay showing the banner slightly for better visual entry animation
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [gaId, user?.id]);

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (consent === 'accepted' && user?.id) {
      setGAUserId(user.id);
    }
  }, [user?.id]);

  useEffect(() => {
    const handleReset = () => {
      setVisible(true);
    };
    window.addEventListener('cookie-consent-reset', handleReset);
    return () => window.removeEventListener('cookie-consent-reset', handleReset);
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookie-consent', 'accepted');
    if (gaId) {
      initGA(gaId);
      if (user?.id) {
        setGAUserId(user.id);
      }
    }
    setVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem('cookie-consent', 'declined');
    setVisible(false);
  };

  if (isRenderMode || !visible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        maxWidth: '300px',
        background: 'rgba(18, 18, 18, 0.85)',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '16px',
        padding: '14px 16px',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        animation: 'slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
        color: '#fff',
      }}
    >
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateY(30px) scale(0.95);
            opacity: 0;
          }
          to {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
        }
      `}</style>
      <div>
        <p style={{ margin: 0, fontSize: '11px', lineHeight: '1.5', color: 'var(--text-secondary)' }}>
          <strong>Trail Render</strong> uses cookies to analyze traffic and optimize your video rendering experience. Read our <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-primary)', textDecoration: 'underline' }}>Privacy Policy</a>.
        </p>
      </div>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '4px' }}>
        <button
          onClick={handleDecline}
          style={{
            background: 'transparent',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: 'var(--text-secondary)',
            fontSize: '11px',
            padding: '5px 12px',
            borderRadius: '10px',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            e.currentTarget.style.color = '#fff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.color = 'var(--text-secondary)';
          }}
        >
          Decline
        </button>
        <button
          onClick={handleAccept}
          style={{
            background: 'var(--accent-gradient)',
            border: 'none',
            color: '#fff',
            fontSize: '11px',
            fontWeight: 600,
            padding: '5px 12px',
            borderRadius: '10px',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(255, 56, 92, 0.2)',
            transition: 'opacity 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '0.9';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
        >
          Accept Cookies
        </button>
      </div>
    </div>
  );
};
